import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import orderService from '../../../services/orderService';
import ecommerceOrderService from '../../../services/webOrderService';
import './CombinedOrders.css';

const CombinedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [orderType, setOrderType] = useState('all'); // 'all', 'in-store', 'ecommerce'
  
  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [perPage] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    search: '',
    per_page: perPage
  });

  useEffect(() => {
    loadCombinedOrders();
  }, [currentPage, filters, orderType]);

  const loadCombinedOrders = async () => {
    try {
      setLoading(true);
      let inStoreOrders = [];
      let ecommerceOrders = [];
      let totalCount = 0;

      // Load in-store orders
      if (orderType === 'all' || orderType === 'in-store') {
        try {
          const inStoreResponse = await orderService.getAllOrders({
            ...filters,
            page: currentPage,
            per_page: perPage
          });
          inStoreOrders = inStoreResponse.data.data.map(order => ({
            ...order,
            type: 'in-store',
            display_number: order.order_number,
            customer_name: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : 'Walk-in Customer',
            customer_email: order.customer?.email || 'N/A',
            customer_phone: order.customer?.phone || 'N/A',
            final_amount: order.total_amount
          }));
          totalCount += inStoreResponse.data.total;
        } catch (error) {
          console.error('Failed to load in-store orders:', error);
        }
      }

      // Load e-commerce orders
      if (orderType === 'all' || orderType === 'ecommerce') {
        try {
          const ecommerceResponse = await ecommerceOrderService.getAllWebOrders({
            ...filters,
            page: currentPage,
            per_page: perPage
          });
          ecommerceOrders = ecommerceResponse.data.data.map(order => ({
            ...order,
            type: 'ecommerce',
            display_number: order.order_number,
            customer_name: `${order.customer_first_name} ${order.customer_last_name}`,
            customer_email: order.customer_email,
            customer_phone: order.customer_phone,
            final_amount: order.total_amount,
            delivery_fee: order.delivery_fee_amount,
            address: order.customer_address
          }));
          totalCount += ecommerceResponse.data.total;
        } catch (error) {
          console.error('Failed to load e-commerce orders:', error);
        }
      }

      // Combine and sort orders by creation date (newest first)
      const combinedOrders = [...inStoreOrders, ...ecommerceOrders].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setOrders(combinedOrders);
      setTotalOrders(totalCount);
      setTotalPages(Math.ceil(totalCount / perPage));
    } catch (error) {
      Swal.fire('Error!', 'Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (order) => {
    try {
      let response;
      if (order.type === 'in-store') {
        response = await orderService.getOrderById(order.id);
      } else {
        response = await ecommerceOrderService.getWebOrderById(order.id);
      }
      setSelectedOrder({
        ...response.data,
        type: order.type
      });
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', 'Failed to load order details', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      payment_method: '',
      search: '',
      per_page: perPage
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status, type) => {
    const baseClass = 'co-status-badge';
    
    if (type === 'ecommerce') {
      switch (status) {
        case 'pending': return `${baseClass} co-pending`;
        case 'confirmed': return `${baseClass} co-processing`;
        case 'processing': return `${baseClass} co-processing`;
        case 'ready_for_pickup': return `${baseClass} co-ready`;
        case 'ready_for_delivery': return `${baseClass} co-ready`;
        case 'out_for_delivery': return `${baseClass} co-out-for-delivery`;
        case 'delivered': return `${baseClass} co-completed`;
        case 'cancelled': return `${baseClass} co-cancelled`;
        default: return `${baseClass} co-pending`;
      }
    }
    
    switch (status) {
      case 'completed': return `${baseClass} co-completed`;
      case 'pending': return `${baseClass} co-pending`;
      case 'processing': return `${baseClass} co-processing`;
      case 'cancelled': return `${baseClass} co-cancelled`;
      case 'refunded': return `${baseClass} co-refunded`;
      default: return `${baseClass} co-pending`;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash': return '💵 Cash';
      case 'card': return '💳 Card';
      case 'uber': return '🚗 Uber';
      case 'pickme': return '🚕 PickMe';
      case 'digital_wallet': return '📱 Digital Wallet';
      case 'bank_transfer': return '🏦 Bank Transfer';
      default: return method;
    }
  };

  const getOrderTypeBadge = (type) => {
    switch (type) {
      case 'in-store': return '🏪 In-Store';
      case 'ecommerce': return '🛒 E-commerce';
      default: return type;
    }
  };

  const getStatusLabel = (status, type) => {
    if (type === 'ecommerce') {
      switch (status) {
        case 'ready_for_pickup': return 'Ready for Pickup';
        case 'ready_for_delivery': return 'Ready for Delivery';
        case 'out_for_delivery': return 'Out for Delivery';
        default: return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`co-pagination-btn ${currentPage === i ? 'co-active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="co-pagination">
        <button
          className="co-pagination-btn"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ««
        </button>
        <button
          className="co-pagination-btn"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          «
        </button>
        
        {startPage > 1 && (
          <>
            <button className="co-pagination-btn" onClick={() => setCurrentPage(1)}>1</button>
            {startPage > 2 && <span className="co-pagination-ellipsis">...</span>}
          </>
        )}
        
        {pages}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="co-pagination-ellipsis">...</span>}
            <button className="co-pagination-btn" onClick={() => setCurrentPage(totalPages)}>
              {totalPages}
            </button>
          </>
        )}
        
        <button
          className="co-pagination-btn"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          »
        </button>
        <button
          className="co-pagination-btn"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          »»
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="co-page-container">
        <div className="co-page-header">
          <h1>Combined Order Management</h1>
          <p>Manage all customer orders from both in-store and e-commerce</p>
        </div>
        <div className="co-loading">
          <div className="co-loading-spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page-container">
      <div className="co-page-header">
        <div className="co-header-content">
          <div className="co-header-text">
            <h1>Combined Order Management</h1>
            <p>
              Manage all customer orders from both in-store and e-commerce
              {totalOrders > 0 && <span className="co-total-count"> ({totalOrders} total orders)</span>}
            </p>
          </div>
          <button className="co-btn co-btn-secondary" onClick={loadCombinedOrders}>
            <span className="co-btn-icon">🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="co-order-type-tabs">
        <button 
          className={`co-tab-btn ${orderType === 'all' ? 'co-active' : ''}`}
          onClick={() => setOrderType('all')}
        >
          <span className="co-tab-icon">📦</span>
          All Orders
        </button>
        <button 
          className={`co-tab-btn ${orderType === 'in-store' ? 'co-active' : ''}`}
          onClick={() => setOrderType('in-store')}
        >
          <span className="co-tab-icon">🏪</span>
          In-Store
        </button>
        <button 
          className={`co-tab-btn ${orderType === 'ecommerce' ? 'co-active' : ''}`}
          onClick={() => setOrderType('ecommerce')}
        >
          <span className="co-tab-icon">🛒</span>
          E-commerce
        </button>
      </div>

      {/* Filters Section */}
      <div className="co-filters-section">
        <div className="co-filters-row">
          <div className="co-filter-group">
            <div className="co-filter-input-wrapper">
              <span className="co-filter-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by order number or customer..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="co-filter-input"
              />
            </div>
          </div>

          <div className="co-filter-group">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="co-filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="ready_for_pickup">Ready for Pickup</option>
              <option value="out_for_delivery">Out for Delivery</option>
            </select>
          </div>

          <div className="co-filter-group">
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="co-filter-select"
            >
              <option value="">All Payment Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="digital_wallet">Digital Wallet</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          <div className="co-filter-actions">
            <button className="co-btn co-btn-clear" onClick={clearFilters} title="Clear Filters">
              <span className="co-btn-icon">🗑️</span>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="co-page-content">
        <div className="co-combined-orders-table-container">
          <div className="co-table-responsive">
            <table className="co-combined-orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Payment Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={`${order.type}-${order.id}`} className={`co-order-row co-${order.type}`}>
                    <td className="co-order-number">
                      <strong>{order.display_number}</strong>
                    </td>
                    <td>
                      <span className="co-order-type-badge">
                        {getOrderTypeBadge(order.type)}
                      </span>
                    </td>
                    <td className="co-order-date">{formatDate(order.created_at)}</td>
                    <td>
                      <div className="co-customer-info">
                        <strong>{order.customer_name}</strong>
                        {order.type === 'ecommerce' && order.address && (
                          <div className="co-customer-address">
                            📍 {order.address}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="co-contact-info">
                        <div className="co-contact-email">📧 {order.customer_email}</div>
                        <div className="co-contact-phone">📞 {order.customer_phone}</div>
                      </div>
                    </td>
                    <td>
                      <div className="co-items-count">
                        {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                      </div>
                      <div className="co-items-preview">
                        {order.items.slice(0, 2).map(item => (
                          <span key={item.id} className="co-item-name">
                            {item.product_name}
                            {item.variant_name && ` (${item.variant_name})`}
                          </span>
                        ))}
                        {order.items.length > 2 && (
                          <span className="co-more-items">+{order.items.length - 2} more</span>
                        )}
                      </div>
                    </td>
                    <td className="co-amount-cell">
                      <strong>{formatCurrency(order.final_amount)}</strong>
                      {order.type === 'ecommerce' && order.delivery_fee > 0 && (
                        <div className="co-delivery-fee">
                          Delivery: {formatCurrency(order.delivery_fee)}
                        </div>
                      )}
                      {order.type === 'in-store' && order.tender_amount && (
                        <div className="co-tender-info">
                          Tender: {formatCurrency(order.tender_amount)}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="co-payment-method">
                        {getPaymentMethodLabel(order.payment_method)}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(order.status, order.type)}>
                        {getStatusLabel(order.status, order.type)}
                      </span>
                    </td>
                    <td>
                      <div className="co-action-buttons">
                        <button 
                          className="co-btn co-btn-view"
                          onClick={() => handleViewOrder(order)}
                          title="View Order Details"
                        >
                          <span className="co-btn-icon">👁️</span>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {orders.length === 0 && (
            <div className="co-no-data">
              <div className="co-no-data-icon">📦</div>
              <h3>No Orders Found</h3>
              <p>
                {filters.status || filters.payment_method || filters.search 
                  ? 'No orders match your current filters.' 
                  : `No ${orderType === 'all' ? '' : orderType + ' '}orders found.`
                }
              </p>
              {(filters.status || filters.payment_method || filters.search) && (
                <button className="co-btn co-btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <ViewOrderModal
          order={selectedOrder}
          onClose={() => setShowViewModal(false)}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusBadgeClass={getStatusBadgeClass}
          getPaymentMethodLabel={getPaymentMethodLabel}
          getStatusLabel={getStatusLabel}
        />
      )}
    </div>
  );
};

// Enhanced View Order Modal Component
const ViewOrderModal = ({ order, onClose, formatDate, formatCurrency, getStatusBadgeClass, getPaymentMethodLabel, getStatusLabel }) => {
  return (
    <div className="co-modal-overlay">
      <div className="co-modal co-modal-large">
        <div className="co-modal-header">
          <div className="co-modal-title">
            <h3>Order Details - {order.order_number}</h3>
            <span className={`co-order-type-badge co-modal-badge co-${order.type}`}>
              {order.type === 'in-store' ? '🏪 In-Store' : '🛒 E-commerce'}
            </span>
          </div>
          <button className="co-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="co-modal-body">
          <div className="co-order-detail-view">
            {/* Order Header */}
            <div className="co-order-header">
              <div className="co-order-meta">
                <div className="co-meta-item">
                  <label>Order Number:</label>
                  <span>{order.order_number}</span>
                </div>
                <div className="co-meta-item">
                  <label>Order Date:</label>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div className="co-meta-item">
                  <label>Status:</label>
                  <span className={getStatusBadgeClass(order.status, order.type)}>
                    {getStatusLabel(order.status, order.type)}
                  </span>
                </div>
                <div className="co-meta-item">
                  <label>Payment Method:</label>
                  <span>{getPaymentMethodLabel(order.payment_method)}</span>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="co-customer-section">
              <h4>Customer Information</h4>
              <div className="co-customer-details">
                {order.type === 'in-store' ? (
                  <>
                    <div className="co-customer-info">
                      <strong>{order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : 'Walk-in Customer'}</strong>
                      {order.customer && (
                        <>
                          <div>📧 {order.customer.email || 'N/A'}</div>
                          <div>📞 {order.customer.phone || 'N/A'}</div>
                        </>
                      )}
                    </div>
                    {order.cashier && (
                      <div className="co-cashier-info">
                        <h5>Cashier</h5>
                        <div className="co-cashier-card">
                          <div className="co-cashier-avatar">
                            {order.cashier.full_legal_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <div className="co-cashier-details">
                            <strong>{order.cashier.full_legal_name}</strong>
                            <div>ID: {order.cashier.employee_id}</div>
                            <div>Role: {order.cashier.user_role}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="co-ecommerce-customer">
                    <div className="co-customer-main">
                      <strong>{order.customer_first_name} {order.customer_last_name}</strong>
                      <div>📧 {order.customer_email}</div>
                      <div>📞 {order.customer_phone}</div>
                    </div>
                    {order.customer_address && (
                      <div className="co-delivery-address">
                        <h5>Delivery Address</h5>
                        <p>{order.customer_address}</p>
                        {order.customer_city && <p>{order.customer_city}, {order.customer_postal_code}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="co-order-summary">
              <h4>Order Summary</h4>
              <div className="co-summary-grid">
                <div className="co-summary-item">
                  <label>Subtotal:</label>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                {order.tax_amount > 0 && (
                  <div className="co-summary-item">
                    <label>Tax:</label>
                    <span>{formatCurrency(order.tax_amount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="co-summary-item">
                    <label>Discount:</label>
                    <span>-{formatCurrency(order.discount_amount)}</span>
                  </div>
                )}
                {order.type === 'ecommerce' && order.delivery_fee_amount > 0 && (
                  <div className="co-summary-item">
                    <label>Delivery Fee:</label>
                    <span>{formatCurrency(order.delivery_fee_amount)}</span>
                  </div>
                )}
                <div className="co-summary-item co-total">
                  <label>Total Amount:</label>
                  <span>{formatCurrency(order.total_amount)}</span>
                </div>
                {order.type === 'in-store' && order.tender_amount && (
                  <div className="co-summary-item">
                    <label>Tender Amount:</label>
                    <span>{formatCurrency(order.tender_amount)}</span>
                  </div>
                )}
                {order.type === 'in-store' && order.change_amount && order.change_amount > 0 && (
                  <div className="co-summary-item">
                    <label>Change Given:</label>
                    <span>{formatCurrency(order.change_amount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div className="co-order-items">
              <h4>Order Items ({order.items.length})</h4>
              <div className="co-items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="co-product-info">
                            <strong>{item.product_name}</strong>
                            {item.product?.brand && (
                              <span className="co-product-brand">{item.product.brand}</span>
                            )}
                          </div>
                        </td>
                        <td>{item.variant_name || 'N/A'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{formatCurrency(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* E-commerce Specific Timeline */}
            {order.type === 'ecommerce' && (
              <div className="co-order-timeline">
                <h4>Order Timeline</h4>
                <div className="co-timeline">
                  {order.confirmed_at && (
                    <div className="co-timeline-item co-confirmed">
                      <div className="co-timeline-dot"></div>
                      <div className="co-timeline-content">
                        <strong>Order Confirmed</strong>
                        <span>{formatDate(order.confirmed_at)}</span>
                      </div>
                    </div>
                  )}
                  {order.processing_at && (
                    <div className="co-timeline-item co-processing">
                      <div className="co-timeline-dot"></div>
                      <div className="co-timeline-content">
                        <strong>Processing</strong>
                        <span>{formatDate(order.processing_at)}</span>
                      </div>
                    </div>
                  )}
                  {order.ready_for_delivery_at && (
                    <div className="co-timeline-item co-ready">
                      <div className="co-timeline-dot"></div>
                      <div className="co-timeline-content">
                        <strong>Ready for Delivery</strong>
                        <span>{formatDate(order.ready_for_delivery_at)}</span>
                      </div>
                    </div>
                  )}
                  {order.out_for_delivery_at && (
                    <div className="co-timeline-item co-out-for-delivery">
                      <div className="co-timeline-dot"></div>
                      <div className="co-timeline-content">
                        <strong>Out for Delivery</strong>
                        <span>{formatDate(order.out_for_delivery_at)}</span>
                      </div>
                    </div>
                  )}
                  {order.delivered_at && (
                    <div className="co-timeline-item co-delivered">
                      <div className="co-timeline-dot"></div>
                      <div className="co-timeline-content">
                        <strong>Delivered</strong>
                        <span>{formatDate(order.delivered_at)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Notes */}
            {(order.notes || order.additional_notes) && (
              <div className="co-order-notes">
                <h4>Order Notes</h4>
                <div className="co-notes-content">
                  {order.notes || order.additional_notes}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="co-modal-footer">
          <button className="co-btn co-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CombinedOrders;